import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { register } from '../api/auth';
import { authStore } from '../api/client';
import LoginNavHeader from '../components/auth/LoginNavHeader';
import LoginShowcasePanel from '../components/auth/LoginShowcasePanel';

function landingPathForRole(role) {
  if (role === 'CANDIDATE') return '/candidate';
  return '/dashboard';
}

function RoleCard({ icon, title, subtitle, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 flex flex-col items-start gap-2 p-3.5 rounded-xl border text-left transition-all ${
        selected
          ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-100'
          : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      <span className="text-xl" aria-hidden="true">{icon}</span>
      <div>
        <div className={`text-sm font-semibold ${selected ? 'login-text-primary' : 'login-text-secondary'}`}>
          {title}
        </div>
        <div className="text-xs login-text-muted mt-0.5">{subtitle}</div>
      </div>
    </button>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('INTERVIEWER');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('route-login');
    return () => document.documentElement.classList.remove('route-login');
  }, []);

  useEffect(() => {
    if (authStore.isAuthenticated) {
      navigate(landingPathForRole(authStore.role), { replace: true });
    }
  }, [navigate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (role !== 'INTERVIEWER' && role !== 'CANDIDATE') {
      setError('Please select a role.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await register({
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        role,
      });
      authStore.set(res.data);
      navigate(landingPathForRole(res.data.user?.role), { replace: true });
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fullName, email, password, confirmPassword, role, navigate]);

  return (
    <>
      <LoginNavHeader />
      <div className="login-page">
        <LoginShowcasePanel />

        <div className="login-form-panel flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8 login-page__safe-top login-page__safe-bottom">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-[420px]"
            >
              <div className="login-form-card rounded-2xl p-7 sm:p-9">
                <div className="mb-7">
                  <h1 className="text-[1.65rem] sm:text-[1.75rem] font-semibold login-text-primary tracking-tight mb-2">
                    Create your account
                  </h1>
                  <p className="text-sm login-text-secondary leading-relaxed">
                    Get started with Samastix in seconds.
                  </p>
                </div>

                {error && (
                  <div className="mb-4 login-error" role="alert">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <span className="text-xs font-medium login-text-secondary mb-2 block">
                      I am a…
                    </span>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <RoleCard
                        icon="💼"
                        title="Interviewer"
                        subtitle="Conduct interviews"
                        selected={role === 'INTERVIEWER'}
                        onClick={() => setRole('INTERVIEWER')}
                      />
                      <RoleCard
                        icon="👤"
                        title="Candidate"
                        subtitle="Take interviews"
                        selected={role === 'CANDIDATE'}
                        onClick={() => setRole('CANDIDATE')}
                      />
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                      Full name
                    </span>
                    <input
                      id="fullName"
                      type="text"
                      required
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Jane Doe"
                      className="login-form-input"
                    />
                  </label>

                  <label className="block">
                    <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                      Work email
                    </span>
                    <input
                      id="regEmail"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="login-form-input"
                    />
                  </label>

                  <label className="block">
                    <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                      Password
                    </span>
                    <div className="relative">
                      <input
                        id="regPassword"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="login-form-input pr-16"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium login-text-muted hover:login-text-primary border-0 bg-transparent cursor-pointer p-0"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-xs font-medium login-text-secondary mb-1.5 block">
                      Confirm password
                    </span>
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      className="login-form-input"
                    />
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    className="login-btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 cursor-pointer min-h-[48px] border-0 mt-2"
                  >
                    {loading ? 'Creating account…' : 'Create account'}
                  </button>
                </form>

                <p className="mt-6 pt-6 border-t border-slate-200/60 text-center text-sm login-text-muted">
                  Already have an account?{' '}
                  <Link to="/login" className="login-text-accent hover:underline font-medium no-underline">
                    Sign in
                  </Link>
                </p>
              </div>

              <p className="mt-5 text-center text-[11px] login-text-faint">
                Protected by enterprise-grade security.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
